import { PRIVILEGES } from 'constants/privileges';
import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import { TransportCloudParams } from 'api/transportCloud/transportCloud.types';
import { LoadingScreen } from 'components/Loaders/LoadingScreen';
import { useAuth } from 'contexts/authContext';
import { useTransportCloudContext } from 'contexts/transportCloudContext';
import { format } from 'date-fns';
import { Navigate } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import { hasPrivilege } from 'utils/authFunctions.utils';
import { appToast } from 'utils/toast.utils';
import { ColumnVisibilityDialog } from './components/Dialogs/ColumnVisibilityDialog';
import { ConfirmDeleteDialog } from './components/Dialogs/ConfirmDeleteDialog';
import {
  MailSenderDialog,
  MailType,
} from './components/Dialogs/MailSenderDialog';
import { ManagementDialog } from './components/Dialogs/ManagementDialog';
import { TableBodyContent } from './components/Table/TableBodyContent';
import { TableControls } from './components/Table/TableControls';
import { TableFilters } from './components/Table/TableFilters';
import { TableHeader } from './components/Table/TableHeader';
import { TableTools } from './components/Table/TableTools';
import { getItemId } from './hooks/cellEditing/cellEditing.utils';
import { useCellEditing } from './hooks/cellEditing/useCellEditing';
import { useColumnVisibility } from './hooks/useColumnVisbility';
import { useDriverDatabaseDialog } from './hooks/useDriverDatabaseDialog';
import { useTableData } from './hooks/useTableData';
import { useTableState } from './hooks/useTableState';
import * as S from './styles';
import {
  FIELD_TO_COLUMN_MAP,
  ERRORS,
  MAIN_HEADERS,
  PAGINATION_ROWS,
  DRIVER_FIELDS,
} from './TransportCloudTable.consts';
import { Item } from './TransportCloudTable.types';

const HTML_DATE_FORMAT = 'yyyy-MM-dd';

const normalizeTruckerValue = (
  val: unknown,
): {
  id: number | null;
  code: string | null;
  counterPartyId: number | null;
} => {
  if (val == null) return { id: null, code: null, counterPartyId: null };
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    const num = (k: string) =>
      typeof obj[k] === 'number' ? (obj[k] as number) : null;
    const str = (k: string) =>
      typeof obj[k] === 'string' ? (obj[k] as string) : null;
    return {
      id: num('id') ?? num('truckerId'),
      code: str('code') ?? str('truckerCode') ?? str('trucker'),
      counterPartyId: num('counterPartyId') ?? num('truckerCounterPartyId'),
    };
  }
  if (typeof val === 'string')
    return { id: null, code: val, counterPartyId: null };
  if (typeof val === 'number')
    return { id: val, code: null, counterPartyId: null };
  return { id: null, code: null, counterPartyId: null };
};

const normalizeContainerStatusValue = (
  val: unknown,
): {
  id: number | null;
  name: string | null;
  color: string | null;
  enabled: boolean;
} => {
  if (val == null) return { id: null, name: null, color: null, enabled: true };
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    return {
      id: typeof obj.id === 'number' ? obj.id : null,
      name: typeof obj.name === 'string' ? obj.name : null,
      color: typeof obj.color === 'string' ? obj.color : null,
      enabled: typeof obj.enabled === 'boolean' ? obj.enabled : true,
    };
  }
  if (typeof val === 'number')
    return { id: val, name: null, color: null, enabled: true };
  return { id: null, name: null, color: null, enabled: true };
};

const createInitialVisibleColumnsState = (): Record<string, boolean> => {
  const initialState: Record<string, boolean> = {};
  MAIN_HEADERS.forEach(([key]) => {
    initialState[key] = true;
  });
  return initialState;
};

const getInitialDateFrom = (
  params: any,
  getTodayFormatted: () => string,
): string => {
  if (params.loadingDeliveryDateFrom) {
    return format(new Date(params.loadingDeliveryDateFrom), HTML_DATE_FORMAT);
  }
  const hasActiveFilters = params.filterText || params.loadingDeliveryDateTo;
  return hasActiveFilters ? '' : getTodayFormatted();
};

export const TransportCloudTable: React.FC = () => {
  const { user, allowedReplacements } = useAuth();

  const canEditAllColumns = hasPrivilege(
    PRIVILEGES.TransportCloud.EDIT_COLUMNS_ALL,
    user,
  );
  const hasForwarderEditPrivilege = hasPrivilege(
    PRIVILEGES.TransportCloud.EDIT_COLUMNS_FORWARDER,
    user,
  );
  const hasTransportEditPrivilege = hasPrivilege(
    PRIVILEGES.TransportCloud.EDIT_COLUMNS_TRANSPORT,
    user,
  );
  const canEditAllRecords = hasPrivilege(
    PRIVILEGES.TransportCloud.EDIT_RECORDS_ALL,
    user,
  );
  const hasOwnerEditPrivilege = hasPrivilege(
    PRIVILEGES.TransportCloud.EDIT_RECORDS_OWNER,
    user,
  );
  const canEditAllShipmentPlans = hasPrivilege(
    PRIVILEGES.TransportCloud.EDIT_SHIPMENT_PLANS_ALL,
    user,
  );
  const hasOwnerEditShipmentPlansPrivilege = hasPrivilege(
    PRIVILEGES.TransportCloud.EDIT_SHIPMENT_PLANS_OWNER,
    user,
  );
  const ownerIds = useMemo(() => {
    const ids = new Set<number>();
    if (user?.id != null) ids.add(user.id);
    allowedReplacements.forEach((id) => ids.add(id));
    return ids;
  }, [user?.id, allowedReplacements]);
  const canDeleteAllRecords = hasPrivilege(
    PRIVILEGES.TransportCloud.DELETE_RECORDS_ALL,
    user,
  );
  const hasOwnerDeletePrivilege = hasPrivilege(
    PRIVILEGES.TransportCloud.DELETE_RECORDS_OWNER,
    user,
  );
  const canDeleteAllShipmentPlans = hasPrivilege(
    PRIVILEGES.TransportCloud.DELETE_SHIPMENT_PLANS,
    user,
  );
  const hasOwnerDeleteShipmentPlansPrivilege = hasPrivilege(
    PRIVILEGES.TransportCloud.DELETE_SHIPMENT_PLANS_OWNER,
    user,
  );
  const hasAnyDeletePrivilege =
    canDeleteAllRecords ||
    hasOwnerDeletePrivilege ||
    canDeleteAllShipmentPlans ||
    hasOwnerDeleteShipmentPlansPrivilege;
  const hasAddShipmentPlansPrivilege = hasPrivilege(
    PRIVILEGES.TransportCloud.ADD_SHIPMENT_PLANS,
    user,
  );
  const canAddAllLoads = hasPrivilege(PRIVILEGES.Jobs.ADD_LOAD, user);
  const hasOwnerAddLoadPrivilege = hasPrivilege(
    PRIVILEGES.Jobs.ADD_LOAD_OWNER,
    user,
  );
  const canManageContainerStatuses = hasPrivilege(
    PRIVILEGES.TransportCloud.MANAGE_CONTAINER_STATUSES,
    user,
  );
  const canManageDriversDatabase = hasPrivilege(
    PRIVILEGES.TransportCloud.MANAGE_DRIVERS_DATABASE,
    user,
  );
  const showManagementButton =
    canManageContainerStatuses || canManageDriversDatabase;
  const canMailDocuments =
    hasPrivilege(PRIVILEGES.Jobs.MAIL_DOCUMENTS, user) ||
    hasPrivilege(PRIVILEGES.Jobs.SEND_CONTAINER_NOTIFICATION, user);
  const canMailDocumentsOwner = hasPrivilege(
    PRIVILEGES.Jobs.MAIL_DOCUMENTS_OWNER,
    user,
  );
  const {
    getTabParams,
    getSetTabParams,
    sendCellUpdate,
    sendCellUpdateNotify,
    sendStartEditing,
    sendStopEditing,
    sendDeleteRows,
    registerCellUpdateHandler,
    registerCleanupEditingHandler,
    lockedCells,
    filterState,
    setFilterState,
    isConnected,
  } = useTransportCloudContext();
  const params = getTabParams();
  const setParams = getSetTabParams();

  const getTodayFormatted = () => format(new Date(), HTML_DATE_FORMAT);
  const tableWrapperRef = useRef<HTMLTableElement>(null);

  const tableState = useTableState(
    params,
    filterState,
    hasTransportEditPrivilege &&
      !canEditAllColumns &&
      !hasForwarderEditPrivilege
      ? false
      : true,
  );
  const columnVisibility = useColumnVisibility(
    createInitialVisibleColumnsState(),
  );
  const tableData = useTableData(params);

  const [managementDialogOpen, setManagementDialogOpen] = useState(false);
  const [userHasInteractedWithDate, setUserHasInteractedWithDate] =
    React.useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mailSenderType, setMailSenderType] = useState<MailType | null>(null);
  const [deletedRows, setDeletedRows] = useState<Set<string>>(new Set());
  const [deletingRows, setDeletingRows] = useState<Set<string>>(new Set());
  const [deleteErrorRows, setDeleteErrorRows] = useState<Set<string>>(
    new Set(),
  );
  const [recentlyDeletedRows, setRecentlyDeletedRows] = useState<
    Map<string, boolean>
  >(new Map());
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const deletingRowsRef = useRef<Set<string>>(new Set());
  const pendingSentValuesRef = useRef<Map<string, any>>(new Map());

  const cellEditing = useCellEditing(
    sendCellUpdate,
    sendStartEditing,
    sendStopEditing,
    sendCellUpdateNotify,
    tableData.groupedRows,
    selected,
    canEditAllRecords,
    hasOwnerEditPrivilege,
    canEditAllShipmentPlans,
    hasOwnerEditShipmentPlansPrivilege,
    ownerIds,
  );

  const cellEditingRef = useRef(cellEditing);
  const groupedRowsRef = useRef(tableData.groupedRows);
  const setGroupedRowsRef = useRef(tableData.setGroupedRows);
  const populateRowsRef = useRef(tableData.populateRows);
  const selectedRef = useRef(selected);

  cellEditingRef.current = cellEditing;
  groupedRowsRef.current = tableData.groupedRows;
  setGroupedRowsRef.current = tableData.setGroupedRows;
  populateRowsRef.current = tableData.populateRows;
  selectedRef.current = selected;
  deletingRowsRef.current = deletingRows;

  const driverDbDialog = useDriverDatabaseDialog(
    groupedRowsRef,
    pendingSentValuesRef,
  );
  const driverDbDialogRef = useRef(driverDbDialog);
  driverDbDialogRef.current = driverDbDialog;

  const stableCellEdit = useCallback(
    (itemId: string, columnKey: string, cellValue: any) =>
      cellEditingRef.current.handleCellEdit(itemId, columnKey, cellValue),
    [],
  );

  const stableCellSave = useCallback(
    (
      itemId: string,
      columnKey: string,
      newValue: any,
      skipDriverCheck?: boolean,
    ) => {
      pendingSentValuesRef.current.set(`${itemId}:${columnKey}`, newValue);
      if (!skipDriverCheck && canManageDriversDatabase) {
        driverDbDialogRef.current.checkRef.current(itemId, columnKey);
      }
      return cellEditingRef.current.handleCellSave(
        itemId,
        columnKey,
        newValue,
        groupedRowsRef.current,
        setGroupedRowsRef.current,
        false,
        skipDriverCheck,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const stableCellCancel = useCallback(
    () => cellEditingRef.current.handleCellCancel(),
    [],
  );

  const stableMultiEditConfirm = useCallback((persistChecked: boolean) => {
    cellEditingRef.current.handleMultiEditConfirm(setGroupedRowsRef.current);
    if (!persistChecked) setSelected([]);
  }, []);

  const stableMultiEditCancel = useCallback(
    () =>
      cellEditingRef.current.handleMultiEditCancel(setGroupedRowsRef.current),
    [],
  );

  const stableMultiEditClickOutside = useCallback(
    () => cellEditingRef.current.handleMultiEditClickOutside(),
    [],
  );

  const stableAcceptDateChange = useCallback(
    (itemId: string) =>
      cellEditingRef.current.handleAcceptDateChange(
        itemId,
        groupedRowsRef.current,
        setGroupedRowsRef.current,
      ),
    [],
  );

  const stableAcceptMultiEditConfirm = useCallback(
    (persistChecked: boolean) => {
      cellEditingRef.current.handleAcceptMultiEditConfirm();
      if (!persistChecked) setSelected([]);
    },
    [],
  );

  const stableAcceptMultiEditCancel = useCallback(
    () =>
      cellEditingRef.current.handleAcceptMultiEditCancel(
        setGroupedRowsRef.current,
      ),
    [],
  );

  const stableAcceptMultiEditClickOutside = useCallback(
    () => cellEditingRef.current.handleAcceptMultiEditClickOutside(),
    [],
  );

  const stablePopulateRows = useCallback(() => populateRowsRef.current(), []);

  const hasStoredFilters = filterState.checkedOnlyMine !== null;

  React.useEffect(() => {
    if (
      !hasStoredFilters &&
      !tableState.loadingDeliveryDateFrom &&
      !params.loadingDeliveryDateFrom
    ) {
      const initialDate = getInitialDateFrom(params, getTodayFormatted);
      tableState.setLoadingDeliveryDateFrom(initialDate);
    }
  }, []);

  useEffect(() => {
    const table = tableWrapperRef.current;
    if (!table || tableData.loading) return;

    let currentHighlightedColumn: string | null = null;

    const applyColumnHighlight = (columnKey: string | null) => {
      if (currentHighlightedColumn === columnKey) return;

      if (currentHighlightedColumn) {
        table.removeAttribute('data-hover-column');
      }

      currentHighlightedColumn = columnKey;

      if (columnKey) {
        table.setAttribute('data-hover-column', columnKey);
      }
    };

    const handleMouseOver = (e: MouseEvent) => {
      const headerCell = (e.target as HTMLElement).closest(
        'th[data-column]',
      ) as HTMLElement;
      if (!headerCell) return;

      const columnKey = headerCell.getAttribute('data-column');
      applyColumnHighlight(columnKey);
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const relatedTarget = e.relatedTarget as HTMLElement;

      if (relatedTarget && table.contains(relatedTarget)) {
        const targetColumn = target
          .closest('[data-column]')
          ?.getAttribute('data-column');
        const relatedColumn = relatedTarget
          .closest('[data-column]')
          ?.getAttribute('data-column');
        if (targetColumn === relatedColumn) return;
      }

      applyColumnHighlight(null);
    };

    const handleMouseLeave = () => applyColumnHighlight(null);

    const tableHeader = table.querySelector('thead');
    if (tableHeader) {
      tableHeader.addEventListener('mouseover', handleMouseOver);
      tableHeader.addEventListener('mouseout', handleMouseOut);
      tableHeader.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (tableHeader) {
        tableHeader.removeEventListener('mouseover', handleMouseOver);
        tableHeader.removeEventListener('mouseout', handleMouseOut);
        tableHeader.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [tableData.loading]);

  useEffect(() => {
    setFilterState({
      searchValue: tableState.searchValue,
      checkedOnlyMine: tableState.checkedOnlyMine,
      loadingDeliveryDateFrom: tableState.loadingDeliveryDateFrom,
      loadingDeliveryDateTo: tableState.loadingDeliveryDateTo,
    });

    setParams((prev: TransportCloudParams) => {
      let dateFromValue: number | undefined;
      let dateToValue: number | undefined;

      if (tableState.loadingDeliveryDateFrom) {
        dateFromValue = new Date(tableState.loadingDeliveryDateFrom).getTime();
      }

      if (tableState.loadingDeliveryDateTo) {
        dateToValue = new Date(tableState.loadingDeliveryDateTo).getTime();
      }
      const hideShippingLineHaulage =
        !columnVisibility.visibleColumns.shippingLineHaulage;

      const filtersChanged =
        prev.onlyMine !== tableState.checkedOnlyMine ||
        prev.filterText !== tableState.searchValue ||
        prev.loadingDeliveryDateFrom !== dateFromValue ||
        prev.loadingDeliveryDateTo !== dateToValue ||
        prev.hideShippingLineHaulage !== hideShippingLineHaulage;
      if (filtersChanged && tableState.page !== 0) {
        tableState.setPage(0);
      }

      return {
        ...prev,
        pageNumber: filtersChanged ? 1 : tableState.page + 1,
        resultSize: tableState.rowsPerPage,
        onlyMine: tableState.checkedOnlyMine,
        filterText: tableState.searchValue,
        loadingDeliveryDateFrom: dateFromValue,
        loadingDeliveryDateTo: dateToValue,
        hideShippingLineHaulage,
      };
    });
  }, [
    tableState.page,
    tableState.rowsPerPage,
    tableState.checkedOnlyMine,
    tableState.searchValue,
    tableState.loadingDeliveryDateFrom,
    tableState.loadingDeliveryDateTo,
    columnVisibility.visibleColumns.shippingLineHaulage,
    setFilterState,
  ]);

  useEffect(() => {
    tableData.debouncedPopulateRows();
  }, [tableData.debouncedPopulateRows]);

  useEffect(
    () => () => {
      tableState.debouncedSearch.cancel();
      tableData.debouncedPopulateRows.cancel();
    },
    [tableState.debouncedSearch, tableData.debouncedPopulateRows],
  );

  useEffect(() => {
    const handler = (message: {
      type: string;
      rowId?: number;
      fieldName?: string;
      newValue?: any;
      error?: string;
      errorCode?: string;
      jobEntryIds?: number[];
      shipmentPlanContainerIds?: number[];
    }) => {
      if (message.type === 'CELL_UPDATED_NOTIFY') {
        if (!message.rowId || !message.fieldName) return;

        let notifiedItemId: string | null = null;
        for (const group of groupedRowsRef.current) {
          for (const jobCodeGroup of group.jobCodeGroups) {
            const found = jobCodeGroup.items.find(
              (item) => item.jobEntryId === message.rowId,
            );
            if (found) {
              notifiedItemId = `job-${found.jobEntryId}`;
              break;
            }
          }
          if (notifiedItemId) break;
        }

        if (!notifiedItemId) {
          for (const group of groupedRowsRef.current) {
            for (const jobCodeGroup of group.jobCodeGroups) {
              const found = jobCodeGroup.items.find(
                (item) => item.shipmentPlanContainerId === message.rowId,
              );
              if (found) {
                notifiedItemId = `plan-${found.shipmentPlanContainerId}`;
                break;
              }
            }
            if (notifiedItemId) break;
          }
        }

        if (notifiedItemId) {
          cellEditingRef.current.handleDateChangeNotify(
            notifiedItemId,
            message.newValue,
            setGroupedRowsRef.current,
          );
        }
        return;
      }

      if (message.type === 'DELETE_ERROR') {
        if (deleteTimeoutRef.current) {
          clearTimeout(deleteTimeoutRef.current);
          deleteTimeoutRef.current = null;
        }

        const currentDeletingRows = new Set(deletingRowsRef.current);
        const wasCurrentUserDelete = currentDeletingRows.size > 0;

        setDeletingRows(new Set());
        setDeleteErrorRows(currentDeletingRows);

        setTimeout(() => {
          setDeleteErrorRows(new Set());
        }, 3000);

        if (wasCurrentUserDelete) {
          const translatedError =
            message.errorCode === 'DATA_INTEGRITY_VIOLATION'
              ? ERRORS.deleteErrorDataIntegrityViolation
              : ERRORS.deleteError;
          appToast.error(translatedError);
        }

        return;
      }

      if (message.type === 'ROWS_DELETED') {
        const deletedItemIds = new Set<string>();

        if (message.jobEntryIds) {
          message.jobEntryIds.forEach((id) => {
            deletedItemIds.add(`job-${id}`);
          });
        }

        if (message.shipmentPlanContainerIds) {
          message.shipmentPlanContainerIds.forEach((id) => {
            deletedItemIds.add(`plan-${id}`);
          });
        }

        if (deleteTimeoutRef.current) {
          clearTimeout(deleteTimeoutRef.current);
          deleteTimeoutRef.current = null;
        }

        const wasCurrentUserDelete = Array.from(deletedItemIds).every((id) =>
          deletingRowsRef.current.has(id),
        );

        setDeletingRows(new Set());

        setSelected([]);

        const recentlyDeletedMap = new Map<string, boolean>();
        deletedItemIds.forEach((id) => {
          recentlyDeletedMap.set(id, wasCurrentUserDelete);
        });
        setRecentlyDeletedRows(recentlyDeletedMap);

        setTimeout(() => {
          setDeletedRows((prev) => {
            const newSet = new Set(prev);
            deletedItemIds.forEach((id) => newSet.add(id));
            return newSet;
          });

          setTimeout(() => {
            setRecentlyDeletedRows(new Map());
          }, 1000);
        }, 300);
        return;
      }

      if (!message.rowId || !message.fieldName) {
        if (message.rowId && message.type === 'CELL_ERROR') {
          let batchItemId: string | null = null;
          for (const group of groupedRowsRef.current) {
            for (const jobCodeGroup of group.jobCodeGroups) {
              const found = jobCodeGroup.items.find(
                (item) => item.jobEntryId === message.rowId,
              );
              if (found) {
                batchItemId = `job-${found.jobEntryId}`;
                break;
              }
            }
            if (batchItemId) break;
          }

          if (!batchItemId) {
            for (const group of groupedRowsRef.current) {
              for (const jobCodeGroup of group.jobCodeGroups) {
                const found = jobCodeGroup.items.find(
                  (item) => item.shipmentPlanContainerId === message.rowId,
                );
                if (found) {
                  batchItemId = `plan-${found.shipmentPlanContainerId}`;
                  break;
                }
              }
              if (batchItemId) break;
            }
          }

          if (batchItemId) {
            const resolvedItemId = batchItemId;
            DRIVER_FIELDS.forEach((field) => {
              cellEditingRef.current.handleCellUpdateResponse(
                resolvedItemId,
                field,
                false,
                undefined,
                message.errorCode,
                setGroupedRowsRef.current,
                true,
                false,
              );
            });
          }
        }
        return;
      }

      const columnKey =
        FIELD_TO_COLUMN_MAP[message.fieldName] || message.fieldName;

      let itemId: string | null = null;

      const currentRows = groupedRowsRef.current;

      for (const group of currentRows) {
        for (const jobCodeGroup of group.jobCodeGroups) {
          const foundItem = jobCodeGroup.items.find(
            (item) => item.jobEntryId === message.rowId,
          );

          if (foundItem) {
            itemId = `job-${foundItem.jobEntryId}`;
            break;
          }
        }
        if (itemId) break;
      }

      if (!itemId) {
        for (const group of currentRows) {
          for (const jobCodeGroup of group.jobCodeGroups) {
            const foundItem = jobCodeGroup.items.find(
              (item) => item.shipmentPlanContainerId === message.rowId,
            );

            if (foundItem) {
              itemId = `plan-${foundItem.shipmentPlanContainerId}`;
              break;
            }
          }
          if (itemId) break;
        }
      }

      if (!itemId) {
        return;
      }

      const success = message.type !== 'CELL_ERROR';

      const isDirectUserEdit =
        cellEditingRef.current.isFieldRecentlySaved(columnKey) ||
        cellEditingRef.current.isPendingEdit(itemId, columnKey);

      const sentValue = pendingSentValuesRef.current.get(
        `${itemId}:${columnKey}`,
      );
      pendingSentValuesRef.current.delete(`${itemId}:${columnKey}`);

      const patchItem = (patch: (item: Item) => Item) =>
        setGroupedRowsRef.current((prevRows) =>
          prevRows.map((group) => ({
            ...group,
            jobCodeGroups: group.jobCodeGroups.map((jobCodeGroup) => ({
              ...jobCodeGroup,
              items: jobCodeGroup.items.map((item) =>
                getItemId(item) === itemId ? patch(item) : item,
              ),
            })),
          })),
        );

      if (columnKey === 'customsClearance' && success && message.newValue) {
        patchItem((item) => ({
          ...item,
          customsClearancePlaceId: message.newValue.placeId || null,
          customsClearancePlaceCode: message.newValue.placeCode || null,
        }));
      }

      if (columnKey === 'trucker' && success) {
        if (message.newValue == null) {
          patchItem((item) => ({
            ...item,
            trucker: null,
            truckerId: null,
            truckerCounterPartyId: null,
          }));
        } else {
          const sent = normalizeTruckerValue(sentValue);
          const received = normalizeTruckerValue(message.newValue);

          const truckerCode = received.code ?? sent.code;
          const truckerCounterPartyId =
            received.counterPartyId ?? sent.counterPartyId ?? sent.id;
          const truckerId = received.id;

          patchItem((item) => {
            const truckerChanged =
              truckerCode != null && truckerCode !== item.trucker;
            return {
              ...item,
              truckerId: truckerId ?? (truckerChanged ? null : item.truckerId),
              truckerCounterPartyId:
                truckerCounterPartyId ??
                (truckerChanged ? null : item.truckerCounterPartyId),
              ...(truckerCode != null ? { trucker: truckerCode } : {}),
            };
          });
        }
      }

      if (columnKey === 'containerStatus' && success && !isDirectUserEdit) {
        if (message.newValue == null) {
          patchItem((item) => ({ ...item, containerStatus: null }));
        } else {
          const received = normalizeContainerStatusValue(message.newValue);

          patchItem((item) => ({
            ...item,
            containerStatus:
              received.id != null
                ? {
                    id: received.id,
                    type: item.tradeType === 'EXPORT' ? 'EXPORT' : 'IMPORT',
                    name: received.name ?? '',
                    color: received.color ?? null,
                    enabled: received.enabled,
                  }
                : null,
          }));
        }
      }

      cellEditingRef.current.handleCellUpdateResponse(
        itemId,
        columnKey,
        success,
        message.newValue,
        message.error || message.errorCode,
        setGroupedRowsRef.current,
        true,
        !isDirectUserEdit,
      );
    };

    registerCellUpdateHandler(handler);

    return () => {
      registerCellUpdateHandler(null);
    };
  }, [registerCellUpdateHandler]);

  useEffect(() => {
    registerCleanupEditingHandler(cellEditing.cleanupEditingState);

    return () => {
      registerCleanupEditingHandler(null);
    };
  }, [cellEditing.cleanupEditingState, registerCleanupEditingHandler]);

  const filteredHeaders = useMemo(
    () =>
      MAIN_HEADERS.filter(
        ([key]) =>
          columnVisibility.visibleColumns[key] ||
          columnVisibility.hidingColumn === key,
      ),
    [columnVisibility.visibleColumns, columnVisibility.hidingColumn],
  );

  const rowCount = useMemo(
    () =>
      tableData.groupedRows.reduce(
        (count, group) =>
          count +
          group.jobCodeGroups.reduce(
            (groupCount, jobCodeGroup) =>
              groupCount + jobCodeGroup.items.length,
            0,
          ),
        0,
      ),
    [tableData.groupedRows],
  );

  const handleDateFromChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUserHasInteractedWithDate(true);
      tableState.handleDateFromChange(event);
    },
    [tableState.handleDateFromChange],
  );

  const handleDateToChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      tableState.handleDateToChange(event);
    },
    [tableState.handleDateToChange],
  );

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      tableState.handleSearch(event);
    },
    [tableState.handleSearch],
  );

  const handleOnlyMineChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      tableState.handleOnlyMineCheckbox(event);
    },
    [tableState.handleOnlyMineCheckbox],
  );

  const handleSelectAllClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const allIds: string[] = [];
        groupedRowsRef.current.forEach((group) => {
          group.jobCodeGroups.forEach((jobCodeGroup) => {
            jobCodeGroup.items.forEach((item) => {
              const itemId = item.jobEntryId
                ? `job-${item.jobEntryId}`
                : item.shipmentPlanContainerId
                ? `plan-${item.shipmentPlanContainerId}`
                : item.lp
                ? `lp-${item.lp}`
                : '';
              if (itemId) {
                allIds.push(itemId);
              }
            });
          });
        });
        setSelected(allIds);
        return;
      }
      setSelected([]);
    },
    [],
  );

  const handleCheckboxClick = useCallback((itemId: string) => {
    setSelected((prev) => {
      const idx = prev.indexOf(itemId);
      if (idx === -1) return [...prev, itemId];
      return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });
  }, []);

  const handleSelectGroup = useCallback((itemIds: string[]) => {
    setSelected((prev) => {
      const prevSet = new Set(prev);
      const allSelected = itemIds.every((id) => prevSet.has(id));
      if (allSelected) {
        const removeSet = new Set(itemIds);
        return prev.filter((id) => !removeSet.has(id));
      }
      const newSelection = [...prev];
      itemIds.forEach((id) => {
        if (!prevSet.has(id)) {
          newSelection.push(id);
          prevSet.add(id);
        }
      });
      return newSelection;
    });
  }, []);

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const currentSelected = selectedRef.current;
    const jobEntryIds: number[] = [];
    const shipmentPlanContainerIds: number[] = [];

    currentSelected.forEach((itemId) => {
      if (itemId.startsWith('job-')) {
        const id = parseInt(itemId.replace('job-', ''), 10);
        if (!isNaN(id)) {
          jobEntryIds.push(id);
        }
      } else if (itemId.startsWith('plan-')) {
        const id = parseInt(itemId.replace('plan-', ''), 10);
        if (!isNaN(id)) {
          shipmentPlanContainerIds.push(id);
        }
      }
    });
    setDeletingRows(new Set(currentSelected));

    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
    }

    deleteTimeoutRef.current = setTimeout(() => {
      appToast.error(ERRORS.deleteError);
      setDeletingRows(new Set());
      deleteTimeoutRef.current = null;
    }, 30000);

    if (sendDeleteRows) {
      sendDeleteRows({ jobEntryIds, shipmentPlanContainerIds });
    }

    setDeleteDialogOpen(false);
  }, [sendDeleteRows]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const hasUnauthorizedSelectedItems = useMemo(() => {
    if (selected.length === 0) return false;
    const itemMap = new Map<string, Item>();
    tableData.groupedRows.forEach((group) => {
      group.jobCodeGroups.forEach((jobCodeGroup) => {
        jobCodeGroup.items.forEach((item) => {
          const id = item.jobEntryId
            ? `job-${item.jobEntryId}`
            : item.shipmentPlanContainerId
            ? `plan-${item.shipmentPlanContainerId}`
            : null;
          if (id) itemMap.set(id, item);
        });
      });
    });
    return selected.some((selectedId) => {
      const item = itemMap.get(selectedId);
      if (!item) return false;
      if (selectedId.startsWith('job-')) {
        return !(
          canDeleteAllRecords ||
          (hasOwnerDeletePrivilege &&
            item.ownerId != null &&
            ownerIds.has(item.ownerId))
        );
      }
      if (selectedId.startsWith('plan-')) {
        return !(
          canDeleteAllShipmentPlans ||
          (hasOwnerDeleteShipmentPlansPrivilege &&
            item.ownerId != null &&
            ownerIds.has(item.ownerId))
        );
      }
      return false;
    });
  }, [
    selected,
    tableData.groupedRows,
    canDeleteAllRecords,
    hasOwnerDeletePrivilege,
    canDeleteAllShipmentPlans,
    hasOwnerDeleteShipmentPlansPrivilege,
    ownerIds,
  ]);

  const mailSenderConditions = useMemo(() => {
    const itemMap = new Map<string, Item>();
    tableData.groupedRows.forEach((group) => {
      group.jobCodeGroups.forEach((jobCodeGroup) => {
        jobCodeGroup.items.forEach((item) => {
          if (item.jobEntryId) {
            itemMap.set(`job-${item.jobEntryId}`, item);
          }
        });
      });
    });

    const hasPermission = canMailDocuments || canMailDocumentsOwner;
    const hasSelection = selected.length > 0;
    const allJobRows = selected.every((id) => id.startsWith('job-'));

    const selectedJobItems = selected
      .map((id) => itemMap.get(id))
      .filter((item): item is Item => item != null && !!item.jobEntryId);

    const sameJob =
      selectedJobItems.length > 0 &&
      selectedJobItems.every(
        (item) => item.jobId === selectedJobItems[0].jobId,
      );

    const allHaveTrucker = selectedJobItems.every(
      (item) => item.truckerId != null || !!item.trucker,
    );

    const ownerAuthorized = selectedJobItems.every(
      (item) =>
        canMailDocuments ||
        (canMailDocumentsOwner &&
          item.ownerId != null &&
          ownerIds.has(item.ownerId)),
    );

    return {
      hasPermission,
      hasSelection,
      allJobRows,
      sameJob,
      allHaveTrucker,
      ownerAuthorized,
    };
  }, [
    selected,
    tableData.groupedRows,
    canMailDocuments,
    canMailDocumentsOwner,
    ownerIds,
  ]);

  const isMailSenderEnabled = useMemo(
    () => Object.values(mailSenderConditions).every(Boolean),
    [mailSenderConditions],
  );

  const isNotificationEnabled = useMemo(() => {
    if (!isMailSenderEnabled) return false;

    const itemMap = new Map<string, Item>();
    tableData.groupedRows.forEach((group) => {
      group.jobCodeGroups.forEach((jobCodeGroup) => {
        jobCodeGroup.items.forEach((item) => {
          if (item.jobEntryId) {
            itemMap.set(`job-${item.jobEntryId}`, item);
          }
        });
      });
    });

    return selected.every((id) => {
      const item = itemMap.get(id);
      return item != null && item.waybillSendDate != null;
    });
  }, [isMailSenderEnabled, selected, tableData.groupedRows]);

  const isWaybillEnabled = useMemo(() => {
    if (!isMailSenderEnabled) return false;

    const itemMap = new Map<string, Item>();
    tableData.groupedRows.forEach((group) => {
      group.jobCodeGroups.forEach((jobCodeGroup) => {
        jobCodeGroup.items.forEach((item) => {
          if (item.jobEntryId) {
            itemMap.set(`job-${item.jobEntryId}`, item);
          }
        });
      });
    });

    return selected.every((id) => {
      const item = itemMap.get(id);
      return item != null && (!!item.containerNumber || !!item.pickupLocation);
    });
  }, [isMailSenderEnabled, selected, tableData.groupedRows]);

  const selectedJobEntryIds = useMemo(
    () =>
      selected
        .filter((id) => id.startsWith('job-'))
        .map((id) => parseInt(id.replace('job-', ''), 10))
        .filter((id) => !isNaN(id)),
    [selected],
  );

  const mailSenderJobId = useMemo(() => {
    const firstJobId = selected.find((id) => id.startsWith('job-'));
    if (!firstJobId) return null;
    for (const group of tableData.groupedRows) {
      for (const jcg of group.jobCodeGroups) {
        for (const item of jcg.items) {
          if (`job-${item.jobEntryId}` === firstJobId) return item.jobId;
        }
      }
    }
    return null;
  }, [selected, tableData.groupedRows]);

  const selectedCounterpartyIds = useMemo(() => {
    const ids = new Set<number>();
    const selectedSet = new Set(selected.filter((id) => id.startsWith('job-')));
    tableData.groupedRows.forEach((group) => {
      group.jobCodeGroups.forEach((jobCodeGroup) => {
        jobCodeGroup.items.forEach((item) => {
          if (item.jobEntryId && selectedSet.has(`job-${item.jobEntryId}`)) {
            if (item.customerCounterPartyId)
              ids.add(item.customerCounterPartyId);
            if (item.truckerCounterPartyId) ids.add(item.truckerCounterPartyId);
          }
        });
      });
    });
    return Array.from(ids);
  }, [selected, tableData.groupedRows]);

  const prevTableLoadingRef = useRef(tableData.loading);

  useEffect(() => {
    if (tableData.loading) {
      cellEditing.clearDateChangedRows();
      setSelected([]);
    } else if (prevTableLoadingRef.current) {
      cellEditing.seedDateChangedRowsFromItems(
        tableData.groupedRows,
        tableData.setGroupedRows,
      );
    }
    prevTableLoadingRef.current = tableData.loading;
  }, [
    tableData.loading,
    tableData.groupedRows,
    cellEditing.clearDateChangedRows,
    cellEditing.seedDateChangedRowsFromItems,
  ]);

  useEffect(() => {
    const existingIds = new Set<string>();
    tableData.groupedRows.forEach((group) => {
      group.jobCodeGroups.forEach((jobCodeGroup) => {
        jobCodeGroup.items.forEach((item) => {
          const id = item.jobEntryId
            ? `job-${item.jobEntryId}`
            : item.shipmentPlanContainerId
            ? `plan-${item.shipmentPlanContainerId}`
            : item.lp
            ? `lp-${item.lp}`
            : '';
          if (id) existingIds.add(id);
        });
      });
    });

    setSelected((prev) => prev.filter((id) => existingIds.has(id)));
  }, [tableData.groupedRows]);

  if (user?.clientPanelUser) {
    return <Navigate to={reactPaths.home} replace />;
  }

  if (!tableData.groupedRows.length && tableData.loading) {
    return (
      <Box>
        <LoadingScreen />
      </Box>
    );
  }

  return (
    <S.TableBox>
      {/* <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          px: 2,
          py: 0.5,
          bgcolor: isConnected ? '#e8f5e9' : '#ffebee',
          borderBottom: '1px solid',
          borderColor: isConnected ? '#c8e6c9' : '#ffcdd2',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: isConnected ? '#4caf50' : '#f44336',
          }}
        />
        <Typography
          variant='caption'
          sx={{ color: isConnected ? '#2e7d32' : '#c62828', fontWeight: 500 }}
        >
          {isConnected ? 'WebSocket: Connected' : 'WebSocket: Disconnected'}
        </Typography>
      </Box> */}
      <TableTools
        showUsers={true}
        currentUser={user?.code || ''}
        selectedCount={selected.length}
        isDeleting={deletingRows.size > 0}
        hasAnyDeletePrivilege={hasAnyDeletePrivilege}
        hasUnauthorizedSelectedItems={hasUnauthorizedSelectedItems}
        hasAddShipmentPlansPrivilege={hasAddShipmentPlansPrivilege}
        isMailSenderEnabled={isMailSenderEnabled}
        mailSenderConditions={mailSenderConditions}
        isNotificationEnabled={isNotificationEnabled}
        isWaybillEnabled={isWaybillEnabled}
        showMailSender={canMailDocuments || canMailDocumentsOwner}
        onDeleteClick={handleDeleteClick}
        onMailSenderClick={(type) => setMailSenderType(type)}
      />
      <S.TableContainer>
        {tableData.error ? (
          <Typography color='error' align='center' sx={{ mb: 2 }}>
            {tableData.error}
          </Typography>
        ) : (
          <>
            <TableFilters
              searchValue={tableState.searchValue}
              checkedOnlyMine={tableState.checkedOnlyMine}
              loadingDeliveryDateFrom={tableState.loadingDeliveryDateFrom}
              loadingDeliveryDateTo={tableState.loadingDeliveryDateTo}
              onSearch={handleSearch}
              onOnlyMineChange={handleOnlyMineChange}
              onDateFromChange={handleDateFromChange}
              onDateToChange={handleDateToChange}
            />

            <TableControls
              rowsPerPageOptions={PAGINATION_ROWS}
              count={tableData.paginationCount}
              rowsPerPage={tableState.rowsPerPage}
              page={tableState.page}
              onPageChange={tableState.handleChangePage}
              onRowsPerPageChange={tableState.handleChangeRowsPerPage}
              onOpenDialog={() => columnVisibility.setDialogOpen(true)}
              onOpenManagementDialog={() => setManagementDialogOpen(true)}
              showIcon='visibility'
              showManagementButton={showManagementButton}
            />

            <S.ScrollableContent>
              <S.InnerTable ref={tableWrapperRef} size='small'>
                <TableHeader
                  headers={filteredHeaders}
                  hidingColumn={columnVisibility.hidingColumn}
                  hidingColumns={columnVisibility.hidingColumns}
                  showingColumns={columnVisibility.showingColumns}
                  onHideColumn={columnVisibility.handleHideColumn}
                  numSelected={selected.length}
                  rowCount={rowCount}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBodyContent
                  groupedRows={tableData.groupedRows}
                  loading={tableData.loading}
                  filteredHeaders={filteredHeaders}
                  hidingColumn={columnVisibility.hidingColumn}
                  hidingColumns={columnVisibility.hidingColumns}
                  showingColumns={columnVisibility.showingColumns}
                  editingCell={cellEditing.editingCell}
                  editingValue={cellEditing.editingValue}
                  recentlyEditedCell={cellEditing.recentlyEditedCell}
                  savingCells={cellEditing.savingCells}
                  validationError={cellEditing.validationError}
                  lockedCells={lockedCells}
                  currentUser={user?.code || ''}
                  canEditAllColumns={canEditAllColumns}
                  hasForwarderEditPrivilege={hasForwarderEditPrivilege}
                  hasTransportEditPrivilege={hasTransportEditPrivilege}
                  canEditAllRecords={canEditAllRecords}
                  hasOwnerEditPrivilege={hasOwnerEditPrivilege}
                  canEditAllShipmentPlans={canEditAllShipmentPlans}
                  hasOwnerEditShipmentPlansPrivilege={
                    hasOwnerEditShipmentPlansPrivilege
                  }
                  ownerIds={ownerIds}
                  canAddAllLoads={canAddAllLoads}
                  hasOwnerAddLoadPrivilege={hasOwnerAddLoadPrivilege}
                  dateChangedRows={cellEditing.dateChangedRows}
                  deletedRows={deletedRows}
                  deletingRows={deletingRows}
                  deleteErrorRows={deleteErrorRows}
                  recentlyDeletedRows={recentlyDeletedRows}
                  multiEditConfirmation={cellEditing.multiEditConfirmation}
                  acceptMultiEditConfirmation={
                    cellEditing.acceptMultiEditConfirmation
                  }
                  driverDbConfirmation={driverDbDialog.confirmation}
                  onCellEdit={stableCellEdit}
                  onCellSave={stableCellSave}
                  onCellCancel={stableCellCancel}
                  onEditValueChange={cellEditing.setEditingValue}
                  onMultiEditConfirm={stableMultiEditConfirm}
                  onMultiEditCancel={stableMultiEditCancel}
                  onMultiEditClickOutside={stableMultiEditClickOutside}
                  onAcceptMultiEditConfirm={stableAcceptMultiEditConfirm}
                  onAcceptMultiEditCancel={stableAcceptMultiEditCancel}
                  onAcceptMultiEditClickOutside={
                    stableAcceptMultiEditClickOutside
                  }
                  onDriverDbConfirm={driverDbDialog.handleConfirm}
                  onDriverDbCancel={driverDbDialog.handleCancel}
                  selectedItems={selected}
                  onCheckboxClick={handleCheckboxClick}
                  onSelectGroup={handleSelectGroup}
                  onReload={stablePopulateRows}
                  onAcceptDateChange={stableAcceptDateChange}
                />
              </S.InnerTable>
            </S.ScrollableContent>
          </>
        )}
      </S.TableContainer>

      <ColumnVisibilityDialog
        open={columnVisibility.dialogOpen}
        visibleColumns={columnVisibility.visibleColumns}
        onClose={() => columnVisibility.setDialogOpen(false)}
        onApply={columnVisibility.handleApplyColumnVisibility}
      />

      <ManagementDialog
        open={managementDialogOpen}
        onClose={() => setManagementDialogOpen(false)}
      />

      <MailSenderDialog
        open={mailSenderType !== null}
        type={mailSenderType ?? 'notification'}
        jobEntryIds={selectedJobEntryIds}
        jobId={mailSenderJobId ?? 0}
        counterpartyIds={selectedCounterpartyIds}
        onClose={() => setMailSenderType(null)}
        onAfterSend={stablePopulateRows}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </S.TableBox>
  );
};
